import {
  type SnapshotQuickJumpDestination,
  type SnapshotQuickJumpDestinationKind,
} from "@/components/dat-snapshot/_helpers/dat-snapshot-quick-jump";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { defaultFilter } from "cmdk";
import { BracesIcon, FolderTreeIcon, ListTreeIcon } from "lucide-react";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type { SnapshotQuickJumpDestination, SnapshotQuickJumpDestinationKind };

interface DatSnapshotQuickJumpProps {
  destinations: SnapshotQuickJumpDestination[];
  disabled?: boolean;
  onSelect: (destination: SnapshotQuickJumpDestination) => void;
}

const MINIMUM_MATCH_SCORE = 0.025;

function filterDestination(value: string, search: string, keywords?: string[]): number {
  const score = defaultFilter(value, search, keywords);

  return score >= MINIMUM_MATCH_SCORE ? score : 0;
}

export function DatSnapshotQuickJump({ destinations, disabled = false, onSelect }: DatSnapshotQuickJumpProps) {
  const { t } = useTranslation("fab");
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const resultListRef = useRef<HTMLDivElement>(null);
  const categories = useMemo(
    () => destinations.filter((destination) => destination.kind === "category"),
    [destinations],
  );
  const properties = useMemo(
    () => destinations.filter((destination) => destination.kind === "property"),
    [destinations],
  );

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      setSelectedValue("");
    }

    setOpen(nextOpen);
  };

  const handleMouseSelect = (destination: SnapshotQuickJumpDestination): void => {
    setSelectedValue("");
    setOpen(false);
    onSelect(destination);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }

    const resultItems = Array.from(
      resultListRef.current?.querySelectorAll<HTMLElement>('[cmdk-item]:not([aria-disabled="true"])') ?? [],
    );
    const selectedItem =
      resultItems.find((resultItem) => resultItem.getAttribute("aria-selected") === "true") ?? resultItems[0];
    const destination = destinations.find(
      (candidate) => String(candidate.start) === selectedItem?.dataset.quickJumpStart,
    );

    if (!selectedItem || !destination) {
      return;
    }

    event.preventDefault();
    onSelect(destination);

    const selectedIndex = resultItems.indexOf(selectedItem);
    const nextItem = resultItems[(selectedIndex + 1) % resultItems.length];
    setSelectedValue(nextItem.dataset.value ?? "");
  };

  const renderDestination = (destination: SnapshotQuickJumpDestination) => {
    const Icon = destination.kind === "category" ? FolderTreeIcon : BracesIcon;

    return (
      <CommandItem
        key={`${destination.kind}-${destination.start}`}
        aria-label={destination.label}
        data-quick-jump-destination={destination.kind}
        data-quick-jump-start={destination.start}
        value={`${destination.label} ${destination.context} ${destination.rawKey}`}
        onSelect={() => handleMouseSelect(destination)}
      >
        <Icon aria-hidden="true" className="text-muted-foreground" />
        <span className="min-w-0">
          <span className="block truncate">{destination.label}</span>
          {destination.context ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span aria-hidden="true" className="text-muted-foreground block truncate text-xs">
                    {destination.context}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{destination.context}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </span>
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          disabled={disabled || destinations.length === 0}
        >
          <ListTreeIcon />
          {t("DatSnapshotQuickJump")}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-88 max-w-[calc(100vw-2rem)] p-0">
        <Command
          label={t("DatSnapshotQuickJumpSearch")}
          filter={filterDestination}
          value={selectedValue}
          onValueChange={setSelectedValue}
        >
          <CommandInput
            aria-label={t("DatSnapshotQuickJumpSearch")}
            placeholder={t("DatSnapshotQuickJumpSearch")}
            onKeyDown={handleSearchKeyDown}
          />
          <CommandList ref={resultListRef} onWheel={(event) => event.stopPropagation()}>
            <CommandEmpty>{t("DatSnapshotQuickJumpNoResults")}</CommandEmpty>
            {categories.length > 0 ? (
              <CommandGroup heading={t("DatSnapshotQuickJumpCategories")}>
                {categories.map(renderDestination)}
              </CommandGroup>
            ) : null}
            {properties.length > 0 ? (
              <CommandGroup heading={t("DatSnapshotQuickJumpProperties")}>
                {properties.map(renderDestination)}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
